import { useShopifyPageData } from './hooks/useShopifyPageData';
import { useShopifyConnectionMutations } from './hooks/useShopifyConnectionMutations';
import { useAppBridge } from '@shopify/app-bridge-react';
import { productLanguage } from '@testfeed/product-language';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ShopifyMainPageView } from './components/ShopifyMainPageView';
import { ShopifyAppContext, buildShopifyAppScopeKey } from './ShopifyAppContext';
import { useShopifyScopeLifecycle } from './hooks/useShopifyScopeLifecycle';
import { useConnectedBanner } from './hooks/useConnectedBanner';
import { resolveInsightStudyConfirmation, seedInsightStudyAuthoring } from './hooks/study-builder/studyAuthoringClient';
import type { TestInsightSetup } from './components/insights/InsightSetupsSection';
import { ShopifyPrimaryContent } from './ShopifyPrimaryContent';
import { renderShopifyNewPagesRoute, ShopifySelfPagedView } from './components/ShopifySelfPagedView';
import {
  buildInsightsViewUrl,
  buildDirectStudyResultsViewUrl,
  buildSettingsViewUrl,
  buildStudyBuilderViewUrl,
  ensureStudyBuilderDraftIdentity,
  buildStudyRunViewUrl,
  buildTestsViewUrl,
  parseShopifyAppView,
  type ShopifyAppViewState,
  type ShopifyStudyBuilderStep,
} from './resultsNavigation';
import { introForView, pageTitleForView } from './shopifyPageCopy';
import { resolveConnectionPresentationState, ShopifyConnectionStatusView, ShopifyPageLoading } from './components/ShopifyConnectionStatus';
import { ShopifyAppNavigation } from './components/ShopifyAppNavigation';

import { ShopifyConnectionRecoveryBanner } from './components/ShopifyConnectionRecoveryBanner';
import { LaunchAdmissionPage } from './components/LaunchAdmissionPage';
import { resolveTestFeedReadinessPresentation } from './components/TestFeedReadinessCard';
import { SettingsRoute } from './components/settings/SettingsRoute';
import { useShopifyUiView } from './hooks/outside/useShopifyUiView';
import { useShopifyStartupCommit } from './hooks/outside/useShopifyStartupCommit';
import { isRetiredStudyView } from './components/RetiredStudyPage';
import { isFeatureEnabled } from '@testfeed/entitlement-contracts';
import type { ShopifyTrackingPageId } from './shopifyTrackingCatalog';

const getBrowserLocationSearch = () => globalThis.window?.location.search ?? '';
const getBrowserLocationPathname = () => globalThis.window?.location.pathname ?? '/';

const pushBrowserHistory = (nextUrl: string) => globalThis.window?.history.pushState(null, '', nextUrl);

export function ShopifyAppContent({
  shopify,
  expectedShop,
  activeShopScopeKey: _activeShopScopeKey,
  isActiveShopScope: _isActiveShopScope,
}: {
  shopify: ReturnType<typeof useAppBridge>;
  expectedShop: string | null;
  activeShopScopeKey: string;
  isActiveShopScope: (scopeKey: string) => boolean;
}) {
  const appShellCopy = productLanguage.shopify.components.appShell;
  const [appView, setAppView] = useState<ShopifyAppViewState>(() => ensureStudyBuilderDraftIdentity(parseShopifyAppView(getBrowserLocationPathname(), getBrowserLocationSearch())));

  const navigateToStudies = useCallback(() => { pushBrowserHistory(buildTestsViewUrl()); setAppView({ view: 'studies' }); }, []);
  const navigateToInsights = useCallback(() => { pushBrowserHistory(buildInsightsViewUrl()); setAppView({ view: 'insights' }); }, []);
  const navigateToSettings = useCallback(() => { pushBrowserHistory(buildSettingsViewUrl()); setAppView({ view: 'settings' }); }, []);
  const { verifiedScope, verifiedShop, readiness, isLoading, entitlements, isDisconnectUnconfirmed } = useShopifyPageData({ shopify, expectedShop });
  const resourceScopeKey = buildShopifyAppScopeKey({
    shopDomain: verifiedShop || expectedShop || 'no-shop',
    orgId: verifiedScope?.orgId ?? null,
    connectionId: verifiedScope?.connectionId ?? null,
  }) + `:${readiness?.connectionActivatedAt ?? 'legacy'}:${readiness?.collectionId ?? 'legacy'}`;
  const { token: resourceScopeToken, isActive: isActiveResourceScope } = useShopifyScopeLifecycle(resourceScopeKey);
  const { handleConnectTestFeed, isConnectingTestFeed } = useShopifyConnectionMutations({
    shopify, verifiedShop, connectionScope: verifiedScope, activeShopScopeKey: resourceScopeToken, isActiveShopScope: isActiveResourceScope,
  });
  const connectionPresentationState = resolveConnectionPresentationState({
    readiness,
    verifiedScope,
    isLoading,
  });
  const isTestFeedConnected = !!verifiedScope;
  useEffect(() => {
    const browserWindow = globalThis.window;
    if (!browserWindow) return undefined;
    const handlePopState = () => setAppView(ensureStudyBuilderDraftIdentity(parseShopifyAppView(getBrowserLocationPathname(), getBrowserLocationSearch())));
    browserWindow.addEventListener('popstate', handlePopState);
    return () => browserWindow.removeEventListener('popstate', handlePopState);
  }, []);
  const { show: showConnectedBanner, dismiss: handleDismissConnectedBanner } = useConnectedBanner(getBrowserLocationSearch);

  const navigateToStudyBuilder = useCallback((options: {
    studyId?: string; draftId?: string; step?: ShopifyStudyBuilderStep;
  } = {}) => {
    // A draft ID minted here belongs to a brand-new test; a caller-supplied ID reopens an existing draft.
    const entry = options.draftId || options.studyId ? {} : ({ entry: 'new' } as const);
    const draftId = options.studyId ? undefined : options.draftId ?? crypto.randomUUID()
    pushBrowserHistory(buildStudyBuilderViewUrl({ ...options, draftId, ...entry }));
    setAppView({
      view: 'study-builder', ...(options.studyId ? { studyId: options.studyId } : {}), draftId, step: options.step ?? 'survey', ...entry,
    });
  }, []);

  const handleSubmitInsightSetup = useCallback<TestInsightSetup['submit']>(async (confirmation, signal) => {
    if (signal?.aborted || !verifiedScope || !isActiveResourceScope(resourceScopeToken)) return 'scope_changed';
    const saved = await seedInsightStudyAuthoring(shopify, confirmation, signal);
    if (signal?.aborted || !isActiveResourceScope(resourceScopeToken)) return 'scope_changed';
    navigateToStudyBuilder({ studyId: saved.studyId });
    return 'saved';
  }, [shopify, verifiedScope, resourceScopeToken, isActiveResourceScope, navigateToStudyBuilder]);

  const handleTestInsightSetup = useMemo<TestInsightSetup>(() => ({
    resolve: async (batchId, productId, signal) => {
      if (signal?.aborted || !verifiedScope || !isActiveResourceScope(resourceScopeToken))
        throw new DOMException('Store scope changed', 'AbortError');
      const confirmation = await resolveInsightStudyConfirmation(shopify, { batchId, productId }, signal);
      if (signal?.aborted || !isActiveResourceScope(resourceScopeToken))
        throw new DOMException('Store scope changed', 'AbortError');
      return confirmation;
    },
    submit: handleSubmitInsightSetup,
  }), [shopify, verifiedScope, resourceScopeToken, isActiveResourceScope, handleSubmitInsightSetup]);

  const navigateToDirectResults = useCallback((studyId: string, runId: string | null) => {
    pushBrowserHistory(buildDirectStudyResultsViewUrl(studyId, runId));
    setAppView({ view: 'direct-study-results', studyId, runId });
  }, []);
  const shopContextValue = {
    shopDomain: verifiedShop || 'no-shop',
    orgId: verifiedScope?.orgId ?? null,
    connectionId: verifiedScope?.connectionId ?? null,
  };
  // Keep the same Settings tree across unlink. Moving it out of the connected wrapper after
  // disablement would unmount the action owner while its authoritative refresh is settling.
  const showConnectionSettings = appView.view === 'settings' || (!verifiedScope && (
    isDisconnectUnconfirmed || readiness?.connectionStatus === 'disabled' || readiness?.code === 'SHOPIFY_COLLECTION_HELD'
  ));
  const canStartConnection = resolveTestFeedReadinessPresentation(readiness, false).canConnect;
  const showLaunchAdmission = !verifiedScope && !showConnectionSettings && verifiedShop && canStartConnection && connectionPresentationState !== 'failed';
  let trackingPage: ShopifyTrackingPageId = appView.view;
  let trackingScreen = 'default';
  let trackingResource = JSON.stringify(appView);
  if (appView.view === 'study-builder') trackingResource = appView.studyId ?? appView.draftId ?? appView.setupId ?? 'new';
  if (appView.view === 'direct-study-results') trackingResource = `${appView.studyId}:${appView.runId ?? 'latest'}`;
  if (isRetiredStudyView(appView)) trackingScreen = 'retired';
  if ((appView.view === 'micro-surveys' && !isFeatureEnabled(entitlements, 'micro_surveys')) ||
      (appView.view === 'pixel-events' && !isFeatureEnabled(entitlements, 'pixel_events'))) trackingScreen = 'unavailable';
  if (showConnectionSettings) { trackingPage = 'settings'; trackingScreen = 'default'; trackingResource = 'settings'; }
  else if (!verifiedScope) {
    trackingPage = showLaunchAdmission ? 'launch-admission' : 'connection-recovery';
    if (connectionPresentationState === 'failed') trackingScreen = 'failed';
    else if (!canStartConnection) trackingScreen = 'blocked';
    trackingResource = trackingPage;
  }
  useShopifyUiView({ shopify, shop: connectionPresentationState === 'checking' ? null : verifiedShop,
    pageId: trackingPage, screenId: trackingScreen, resourceKey: `${verifiedScope?.orgId ?? ''}:${verifiedScope?.connectionId ?? ''}:${trackingResource}`,
    unconnected: Boolean(showLaunchAdmission) });
  let startupContent: 'connected-shell' | 'settings' | 'launch-admission' | 'connection-recovery' = 'connected-shell';
  if (showConnectionSettings) startupContent = 'settings';
  else if (showLaunchAdmission) startupContent = 'launch-admission';
  else if (!verifiedScope) startupContent = 'connection-recovery';
  const startupShop = isLoading || connectionPresentationState === 'checking' || connectionPresentationState === 'failed' ? null : verifiedShop;
  useShopifyStartupCommit(shopify, startupShop, startupContent);
  if (showConnectionSettings) return (
    <ShopifySelfPagedView
      shopContextValue={shopContextValue} shopify={shopify} verifiedScope={verifiedScope}
      entitlements={entitlements} onOpenResults={navigateToDirectResults}
      showConnectedBanner={showConnectedBanner} onDismissConnectedBanner={handleDismissConnectedBanner}
      connectedBannerTitle={appShellCopy.connectedBannerTitle} connectedBannerBody={appShellCopy.connectedBannerBody}
      connectionRecoverySection={null}
    >
      <SettingsRoute key={expectedShop} shopify={shopify} expectedShop={expectedShop} />
    </ShopifySelfPagedView>
  );
  if (connectionPresentationState === 'checking') return (
    <>
      <ShopifyAppNavigation entitlements={entitlements} />
      <ShopifyPageLoading view={appView.view} />
    </>
  );
  // Every product deep link shares this gate; Shopify bootstrap and account linking stay outside it.
  if (!verifiedScope) {
    let recovery = <ShopifyConnectionStatusView state={connectionPresentationState} shopify={shopify}
      readiness={readiness} onConnect={handleConnectTestFeed} isConnecting={isConnectingTestFeed} />;
    if (showLaunchAdmission && verifiedShop) {
      recovery = <LaunchAdmissionPage key={verifiedShop} shopify={shopify} shop={verifiedShop} />;
    }
    return (
      <ShopifyAppContext.Provider value={shopContextValue}>
        <ShopifyConnectionRecoveryBanner shopify={shopify} shop={verifiedShop || expectedShop || 'no-shop'}
          readiness={null} onConnect={handleConnectTestFeed} isConnecting={isConnectingTestFeed}
          onSettings={navigateToSettings} isCurrent={() => isActiveResourceScope(resourceScopeToken)} />
        {recovery}
      </ShopifyAppContext.Provider>
    );
  }

  const isHomeView = appView.view === 'home';
  const pageTitle = isHomeView ? undefined : pageTitleForView(appView.view);
  const pageIntro = introForView(appView.view);
  const pageSubtitle = isHomeView ? undefined : pageIntro.body;
  const isStudyBuilderView = appView.view === 'study-builder';
  const isFullWidthView = isStudyBuilderView || appView.view === 'studies' || appView.view === 'insights' || appView.view === 'home';

  const primaryContent = (
    <ShopifyPrimaryContent
      appView={appView} shopify={shopify} entitlements={entitlements}
      onTestSetup={handleTestInsightSetup}
    />
  );

  const connectionRecoverySection = <ShopifyConnectionRecoveryBanner
    shopify={shopify} shop={verifiedShop || expectedShop || 'no-shop'} readiness={readiness}
    onConnect={handleConnectTestFeed} isConnecting={isConnectingTestFeed}
    onSettings={navigateToSettings} isCurrent={() => isActiveResourceScope(resourceScopeToken)}
  />;


  // Tests, Settings, Home, Insights, and Product
  // insights each own their own <s-page> (the New pages standard); they bypass
  // ShopifyMainPageView's <s-page> instead of nesting inside it, the same way the results view
  // already does above.
  const newPagesRoute = renderShopifyNewPagesRoute({
    appView, shopContextValue, shopify, verifiedScope,
    entitlements, onOpenResults: navigateToDirectResults,
    showConnectedBanner, onDismissConnectedBanner: handleDismissConnectedBanner,
    connectedBannerTitle: appShellCopy.connectedBannerTitle, connectedBannerBody: appShellCopy.connectedBannerBody,
    connectionRecoverySection,
    onCreateTest: () => navigateToStudyBuilder({ step: 'survey' }),
    isTestFeedConnected, onConnectTestFeed: handleConnectTestFeed,
    navigateToStudyBuilder,
    onTestSetup: handleTestInsightSetup,
    navigateToInsights,
    navigateToStudies,
    navigateToSettings,
    navigateToDirectResults,
    // The Study Builder route mounts its own builder and routes a saved setup to the run page.
    expectedShop,
    onOpenDirectResults: navigateToDirectResults,
    onStudyPrepared: (studyId) => {
      pushBrowserHistory(buildStudyRunViewUrl({ setupId: studyId, studyId }));
      setAppView({ view: 'study-run', setupId: studyId, studyId });
    },
    onBackToStudies: navigateToStudies,
    readiness, isLoadingReadiness: isLoading,
  });
  if (newPagesRoute) return newPagesRoute;

  return (
    <ShopifyMainPageView
      shopContextValue={shopContextValue} shopify={shopify} verifiedScope={verifiedScope}
      entitlements={entitlements} pageTitle={pageTitle} pageSubtitle={pageSubtitle}
      isFullWidthView={isFullWidthView} showConnectedBanner={showConnectedBanner} onDismissConnectedBanner={handleDismissConnectedBanner}
      connectedBannerTitle={appShellCopy.connectedBannerTitle} connectedBannerBody={appShellCopy.connectedBannerBody}
      connectionRecoverySection={connectionRecoverySection} primaryContent={primaryContent} onOpenResults={navigateToDirectResults}
    />
  );
}
